import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import InvoiceListLayer from "../components/InvoiceListLayer";
import { API_URL, LOCATION_ID } from "..";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import Select from "react-select";

const staffSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    height: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#111827" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(17, 24, 39, 0.08)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#111827" : "#d1d5db",
    },
    fontSize: "14px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "44px",
    padding: "0 12px",
  }),
  input: (base) => ({
    ...base,
    margin: "0px",
    color: "#111827",
    fontSize: "14px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "44px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
    fontSize: "14px",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    padding: "6px",
    maxHeight: "220px",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: "8px",
    fontSize: "13px",
    padding: "8px 12px",
    backgroundColor: state.isSelected
      ? "#111827"
      : state.isFocused
      ? "#f3f4f6"
      : "transparent",
    color: state.isSelected ? "#ffffff" : "#1f2937",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#e5e7eb",
    },
  }),
};

const InvoiceListPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalInvoicesCount: 0,
    totalInvoiceAmountCount: 0,
    paidInvoiceCount: 0,
    paidInvoiceAmountCount: 0,
    overdueInvoiceCount: 0,
    overdueInvoiceAmountCount: 0,
    halfPaidInvoicesCount: 0,
  });

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(formatDate(firstDay));
  const [toDate, setToDate] = useState(formatDate(today));
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isAdmin = currentUser?.roles?.role === "admin" || currentUser?.role === "admin";

  const filteredStaffList = staffList.filter((staff) => {
    if (!selectedCity) return true;
    const staffCityId = staff.cityId ?? staff.city?.id;
    return (
      staffCityId !== undefined &&
      staffCityId !== null &&
      String(staffCityId) === String(selectedCity)
    );
  });

  const staffOptions = [
    { value: "", label: "All Staff" },
    ...filteredStaffList.map((staff) => ({
      value: staff.crm_id || String(staff.id),
      label: staff.name || staff.email,
    })),
  ];

  const cityOptions = [
    { value: "", label: "All Cities" },
    ...cities.map((city) => ({
      value: String(city.id),
      label: city.name,
    })),
  ];

  const fetchCities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/city`);
      const resData = await res.json();
      setCities(Array.isArray(resData) ? resData : []);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/fetch`);
      const resData = await res.json();
      if (resData && Array.isArray(resData.data)) {
        const sorted = [...resData.data].sort((a, b) =>
          (a.name || a.email || "").localeCompare(b.name || b.email || "")
        );
        setStaffList(sorted);
      }
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  const defaultSummary = {
    totalInvoicesCount: 0,
    totalInvoiceAmountCount: 0,
    paidInvoiceCount: 0,
    paidInvoiceAmountCount: 0,
    overdueInvoiceCount: 0,
    overdueInvoiceAmountCount: 0,
    halfPaidInvoicesCount: 0,
  };

  const formatAmount = (amount) => {
    const value = Number(amount || 0);
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchDashboardSummary = async (from, to, city = selectedCity, staff = selectedStaff) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const userIsAdmin = user?.roles?.role === "admin" || user?.role === "admin";
      const cityQuery = userIsAdmin && city ? `&cityId=${city}` : "";
      const staffQuery = userIsAdmin && staff ? `&staffId=${staff}` : "";
      let url;

      if (userIsAdmin) {
        url = `${API_URL}/api/invoice/dashboard?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}${cityQuery}${staffQuery}`;
      } else {
        url = `${API_URL}/api/invoice/dashboard/user/${user?.crm_id}?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
      }

      const api = await fetch(url, { method: "GET" });
      const json = await api.json();

      if (api.status === 200) {
        setSummary(json?.summary || defaultSummary);
      } else {
        setSummary(defaultSummary);
      }
    } catch (error) {
      console.error("Dashboard summary error:", error);
      setSummary(defaultSummary);
    }
  };

  const fetchInvoices = async (from, to, city = selectedCity, staff = selectedStaff) => {
    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user) {
        window.location.href = "/sign-in";
        return;
      }

      const headersList = {
        Accept: "*/*",
        "Content-Type": "application/json",
      };

      const userIsAdmin = user?.roles?.role === "admin" || user?.role === "admin";
      const cityQuery = userIsAdmin && city ? `&cityId=${city}` : "";
      const staffQuery = userIsAdmin && staff ? `&staffId=${staff}` : "";
      let url;

      if (userIsAdmin) {
        url = `${API_URL}/api/invoice/get/invoice?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}${cityQuery}${staffQuery}`;
      } else {
        url = `${API_URL}/api/invoice/get/user/invoice/${user?.crm_id}?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: headersList,
      });

      const json = await response.json();
      setData(json?.invoices || json || []);
    } catch (error) {
      console.error("Fetch invoices error:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(fromDate, toDate, selectedCity, selectedStaff);
    fetchDashboardSummary(fromDate, toDate, selectedCity, selectedStaff);
    fetchCities();
    if (isAdmin) {
      fetchStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    fetchInvoices(fromDate, toDate, selectedCity, selectedStaff);
    fetchDashboardSummary(fromDate, toDate, selectedCity, selectedStaff);
  };

  const summaryCards = [
    {
      title: "Total Order Forms",
      value: summary.totalInvoicesCount || 0,
      amount: summary.totalInvoiceAmountCount,
      to: `/invoices/all?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`,
      icon: "mdi:file-document-multiple-outline",
      tone: "primary",
    },
    {
      title: "Paid Order Forms",
      value: summary.paidInvoiceCount || 0,
      amount: summary.paidInvoiceAmountCount,
      to: `/invoices/paid?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`,
      icon: "mdi:check-circle-outline",
      tone: "success",
    },
    {
      title: "Partially Paid",
      value: summary.halfPaidInvoicesCount || 0,
      amount: null,
      to: `/invoices/partially-paid?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`,
      icon: "mdi:progress-clock",
      tone: "warning",
    },
    {
      title: "Overdue Order Forms",
      value: summary.overdueInvoiceCount || 0,
      amount: summary.overdueInvoiceAmountCount,
      to: `/invoices/overdue?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`,
      icon: "mdi:alert-circle-outline",
      tone: "danger",
    },
  ];

  return (
    <MasterLayout>
      <Breadcrumb title="Order Forms" />

      <div className="invoice-page-responsive">
        <style>
          {`
            .invoice-page-responsive {
              --invoice-primary: #111827;
              --invoice-primary-hover: #1f2937;
              --invoice-border: #e5e7eb;
              --invoice-muted: #6b7280;
              --invoice-soft: #f9fafb;
              --invoice-card: #ffffff;
              --invoice-success: #16a34a;
              --invoice-warning: #d97706;
              --invoice-danger: #dc2626;
            }

            .invoice-filter-card {
              background: var(--invoice-card);
              border: 1px solid var(--invoice-border);
              border-radius: 18px;
              padding: 18px;
              margin-bottom: 18px;
              box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
            }

            .invoice-filter-grid {
              display: grid;
              grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
              align-items: end;
              gap: 14px;
            }

            .invoice-filter-grid.has-staff {
              grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
            }

            .invoice-filter-title {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 14px;
              color: var(--invoice-primary);
              font-size: 15px;
              font-weight: 850;
            }

            .invoice-filter-title-icon {
              width: 34px;
              height: 34px;
              border-radius: 11px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: var(--invoice-primary);
              background: #f3f4f6;
              border: 1px solid var(--invoice-border);
              flex: 0 0 auto;
            }

            .invoice-field-label {
              color: #374151;
              font-size: 12px;
              font-weight: 850;
              margin-bottom: 7px;
              letter-spacing: 0.02em;
            }

            .invoice-filter-card .form-control {
              min-height: 44px;
              border-radius: 12px;
              border: 1px solid var(--invoice-border);
              color: var(--invoice-primary);
              box-shadow: none;
              font-size: 14px;
            }

            .invoice-filter-card .form-control:focus {
              border-color: var(--invoice-primary);
              box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
            }

            .invoice-filter-button {
              min-height: 44px;
              border: 1px solid var(--invoice-primary);
              background: var(--invoice-primary);
              color: #ffffff;
              border-radius: 12px;
              padding: 10px 20px;
              font-size: 14px;
              font-weight: 850;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              transition: all 0.15s ease;
              white-space: nowrap;
            }

            .invoice-filter-button:hover {
              background: var(--invoice-primary-hover);
              border-color: var(--invoice-primary-hover);
              transform: translateY(-1px);
            }

            .invoice-summary-section {
              margin: 0 0 20px;
            }

            .invoice-summary-grid {
              display: grid;
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: 14px;
            }

            .invoice-summary-card {
              display: block;
              height: 100%;
              text-decoration: none;
              color: inherit;
              background: #fff;
              border: 1px solid var(--invoice-border);
              border-radius: 18px;
              padding: 16px;
              box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
              transition: all 0.16s ease;
              overflow: hidden;
              position: relative;
            }

            .invoice-summary-card:hover {
              transform: translateY(-2px);
              border-color: #cbd5e1;
              box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08);
              color: inherit;
            }

            .invoice-summary-card::after {
              content: "";
              position: absolute;
              right: -28px;
              top: -28px;
              width: 86px;
              height: 86px;
              border-radius: 999px;
              background: rgba(17, 24, 39, 0.04);
            }

            .invoice-summary-content {
              position: relative;
              z-index: 1;
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 14px;
            }

            .invoice-summary-left {
              min-width: 0;
            }

            .invoice-summary-label {
              color: var(--invoice-muted);
              font-size: 13px;
              font-weight: 750;
              margin-bottom: 8px;
              line-height: 1.25;
            }

            .invoice-summary-value {
              color: var(--invoice-primary);
              font-size: 26px;
              font-weight: 950;
              line-height: 1;
              letter-spacing: -0.04em;
            }

            .invoice-summary-amount {
              color: var(--invoice-muted);
              font-size: 12px;
              font-weight: 750;
              margin-top: 8px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 150px;
            }

            .invoice-summary-icon {
              width: 44px;
              height: 44px;
              border-radius: 14px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: #fff;
              flex: 0 0 auto;
              font-size: 23px;
            }

            .invoice-summary-icon.primary {
              background: var(--invoice-primary);
            }

            .invoice-summary-icon.success {
              background: var(--invoice-success);
            }

            .invoice-summary-icon.warning {
              background: var(--invoice-warning);
            }

            .invoice-summary-icon.danger {
              background: var(--invoice-danger);
            }

            .invoice-loading-card,
            .invoice-empty-card {
              border: 1px solid var(--invoice-border);
              border-radius: 18px;
              background: #fff;
              padding: 28px 18px;
              text-align: center;
              box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
            }

            .invoice-empty-icon {
              width: 56px;
              height: 56px;
              border-radius: 18px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              background: #f3f4f6;
              color: var(--invoice-muted);
              margin-bottom: 12px;
              font-size: 28px;
            }

            @media (max-width: 1199.98px) {
              .invoice-summary-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
              }
            }

            @media (max-width: 767.98px) {
              .invoice-filter-card {
                border-radius: 14px;
                padding: 14px;
                margin-bottom: 14px;
              }

              .invoice-filter-grid,
              .invoice-filter-grid.has-staff {
                grid-template-columns: 1fr;
                gap: 12px;
              }

              .invoice-filter-button {
                width: 100%;
              }

              .invoice-summary-grid {
                grid-template-columns: 1fr;
                gap: 12px;
              }

              .invoice-summary-card {
                border-radius: 14px;
                padding: 15px;
              }

              .invoice-summary-value {
                font-size: 24px;
              }

              .invoice-summary-icon {
                width: 42px;
                height: 42px;
                border-radius: 13px;
              }

              .invoice-summary-amount {
                max-width: 100%;
              }
            }

            @media (max-width: 420px) {
              .invoice-summary-content {
                gap: 10px;
              }

              .invoice-summary-label {
                font-size: 12px;
              }

              .invoice-summary-value {
                font-size: 22px;
              }
            }
          `}
        </style>

        <div className="invoice-filter-card">
          <div className="invoice-filter-title">
            <span className="invoice-filter-title-icon">
              <Icon icon="mdi:calendar-filter-outline" />
            </span>
            <span>Filter order forms by date{isAdmin ? ", city & staff" : " & city"}</span>
          </div>

          <div className={`invoice-filter-grid ${isAdmin ? "has-staff" : ""}`}>
            <div>
              <label className="invoice-field-label">From</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div>
              <label className="invoice-field-label">To</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            {isAdmin && (
              <div>
                <label className="invoice-field-label">City</label>
                <Select
                  isSearchable
                  options={cityOptions}
                  value={
                    cityOptions.find(
                      (opt) => String(opt.value) === String(selectedCity)
                    ) || cityOptions[0]
                  }
                  onChange={(option) => {
                    const newCity = option ? option.value : "";
                    setSelectedCity(newCity);

                    let nextStaff = selectedStaff;
                    if (newCity && selectedStaff) {
                      const currentStaffObj = staffList.find(
                        (s) =>
                          (s.crm_id && String(s.crm_id) === String(selectedStaff)) ||
                          String(s.id) === String(selectedStaff)
                      );
                      const staffCityId = currentStaffObj?.cityId ?? currentStaffObj?.city?.id;
                      if (!currentStaffObj || String(staffCityId) !== String(newCity)) {
                        nextStaff = "";
                        setSelectedStaff("");
                      }
                    }

                    fetchInvoices(fromDate, toDate, newCity, nextStaff);
                    fetchDashboardSummary(fromDate, toDate, newCity, nextStaff);
                  }}
                  placeholder="Search City..."
                  styles={staffSelectStyles}
                />
              </div>
            )}

            {isAdmin && (
              <div>
                <label className="invoice-field-label">Staff</label>
                <Select
                  isSearchable
                  options={staffOptions}
                  value={
                    staffOptions.find(
                      (opt) => String(opt.value) === String(selectedStaff)
                    ) || staffOptions[0]
                  }
                  onChange={(option) => {
                    const newStaff = option ? option.value : "";
                    setSelectedStaff(newStaff);
                    fetchInvoices(fromDate, toDate, selectedCity, newStaff);
                    fetchDashboardSummary(fromDate, toDate, selectedCity, newStaff);
                  }}
                  placeholder="Search Staff..."
                  styles={staffSelectStyles}
                />
              </div>
            )}

            <div>
              <button
                className="invoice-filter-button"
                type="button"
                onClick={handleFilter}
                disabled={loading}
              >
                <Icon icon="mdi:tune-variant" />
                {loading ? "Filtering..." : "Filter"}
              </button>
            </div>
          </div>
        </div>

        <div className="invoice-summary-section">
          <div className="invoice-summary-grid">
            {summaryCards.map((card) => (
              <Link className="invoice-summary-card" to={card.to} key={card.title}>
                <div className="invoice-summary-content">
                  <div className="invoice-summary-left">
                    <div className="invoice-summary-label">{card.title}</div>
                    <div className="invoice-summary-value">{card.value}</div>
                    {card.amount !== null && card.amount !== undefined && (
                      <div className="invoice-summary-amount">
                        Amount: {formatAmount(card.amount)}
                      </div>
                    )}
                  </div>

                  <span className={`invoice-summary-icon ${card.tone}`}>
                    <Icon icon={card.icon} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {loading && (
          <div className="invoice-loading-card">
            <Spinner />
            <div className="mt-3 text-muted fw-medium">Loading order forms...</div>
          </div>
        )}

        {!loading && data.length === 0 && (
          <div className="invoice-empty-card">
            <div className="invoice-empty-icon">
              <Icon icon="mdi:file-document-alert-outline" />
            </div>
            <h6 className="mb-1 fw-bold">No order forms found</h6>
            <p className="mb-0 text-muted">Try changing the date range or create a new order form.</p>
          </div>
        )}

        {!loading && data.length > 0 && <InvoiceListLayer jobsData={data} />}
      </div>
    </MasterLayout>
  );
};

export default InvoiceListPage;
